import { setConfig, getConfig, close } from "./config/index";
import { initHttpClient, setAuthToken } from "./utils/httpClient";
import { requestRevocation, getRevocationReasons } from "./api/revocations";
import {
  generateCert,
  getCertStatus,
  getCertTypes,
  getRequerimentsByTypeUser,
} from "./api/certs";
import {
  addSigner,
  deleteSignatureVariant,
  getDigest,
  getDocs,
  getSignatureVariant,
  signDocument,
  uploadSignatureVariant,
} from "./api/signatures";
import {
  createFaceLivenessSession,
  validateFaceLiveness,
} from "./api/faceLiveness";
import ApacuanaSuccess from "./success/index";
import { ApacuanaAPIError } from "./errors/index";
import { createApacuanaUser, getCustomer } from "./api/users";

/**
 * Valida si el SDK está inicializado y si se dispone de un customerId.
 * @param {boolean} requiresCustomerId - Indica si la operación necesita un customerId.
 * @throws {ApacuanaAPIError} Si el SDK no está inicializado o el customerId es requerido y no está presente.
 */
const checkSdk = (requiresCustomerId = true) => {
  const config = getConfig();

  if (!config || !config.apiUrl) {
    throw new ApacuanaAPIError(
      "El SDK no está inicializado. Llama a apacuana.init() primero.",
      400,
      "NOT_INITIALIZED_ERROR"
    );
  }

  if (requiresCustomerId && !config.customerId) {
    throw new ApacuanaAPIError(
      "Se requiere un CustomerId para esta operación. Por favor, inicia sesión.",
      403,
      "CUSTOMER_ID_REQUIRED"
    );
  }
};

const apacuana = {
  /**
   * Inicializa el Apacuana SDK con la configuración necesaria.
   */
  init: async (config) => {
    try {
      setConfig(config);
      initHttpClient();
      const currentConfig = getConfig();

      if (currentConfig.customerId) {
        const customer = await getCustomer();
        const { token, userData } = customer.data;
        setConfig({ ...currentConfig, token, userData });
        setAuthToken(token);
        return new ApacuanaSuccess({
          initialized: true,
          message: "SDK inicializado con sesión de usuario.",
        });
      }

      return new ApacuanaSuccess({
        initialized: true,
        message: "SDK inicializado para operaciones públicas (sin customerId).",
      });
    } catch (error) {
      console.error("Error durante la inicialización del SDK:", error);
      if (error instanceof ApacuanaAPIError) {
        throw error;
      }
      throw new ApacuanaAPIError(
        error.message || "Error desconocido durante la inicialización.",
        500,
        error
      );
    }
  },

  // --- Métodos de Revocación ---
  requestRevocation: (data) => {
    checkSdk(true);
    return requestRevocation(data);
  },
  getRevocationReasons: () => {
    checkSdk(true);
    return getRevocationReasons();
  },

  // --- Métodos de Certificados ---
  generateCert: (data) => {
    checkSdk(true);
    return generateCert(data);
  },
  getCertStatus: (isCertificateInDevice) => {
    checkSdk(true);
    return getCertStatus(isCertificateInDevice);
  },
  getCertTypes: () => {
    checkSdk(false);
    return getCertTypes();
  },
  getRequerimentsByTypeUser: (data) => {
    checkSdk(false);
    return getRequerimentsByTypeUser(data);
  },

  // --- Métodos de Firmas y Documentos ---
  addSigner: (data) => {
    checkSdk(true);
    return addSigner(data);
  },
  deleteSignatureVariant: () => {
    checkSdk(true);
    return deleteSignatureVariant();
  },
  getDigest: (data) => {
    checkSdk(true);
    return getDigest(data);
  },
  getDocs: (data) => {
    checkSdk(true);
    return getDocs(data);
  },
  getSignatureVariant: () => {
    checkSdk(true);
    return getSignatureVariant();
  },
  signDocument: (data) => {
    checkSdk(true);
    return signDocument(data);
  },
  uploadSignatureVariant: (data) => {
    checkSdk(true);
    return uploadSignatureVariant(data);
  },

  getCustomer: () => {
    checkSdk(true);
    return getCustomer();
  },
  createFaceLivenessSession: () => {
    checkSdk(true);
    return createFaceLivenessSession();
  },
  createApacuanaUser: (data) => {
    checkSdk(false);
    return createApacuanaUser(data);
  },
  validateFaceLiveness: (data) => {
    checkSdk(true);
    return validateFaceLiveness(data);
  },
  // validateCertificate: (data) => {
  //   checkSdk(true);
  //   validateCertificate(data);
  // },

  close: () => close(),
  getConfig,
};

export default apacuana;
